import { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// Session timeout in milliseconds (30 minutes of inactivity)
const SESSION_TIMEOUT = 30 * 60 * 1000;
// Re-verify subscription every 15 minutes when active (less aggressive)
const REVERIFY_INTERVAL = 15 * 60 * 1000;

// Context to manage site-wide authentication
const SiteAuthContext = createContext(null);

export const useSiteAuth = () => useContext(SiteAuthContext);

export function SiteAuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const lastActivityRef = useRef(Date.now());
  const reverifyIntervalRef = useRef(null);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Verify token and subscription status
  const verifyAuth = useCallback(async (showLoading = false) => {
    const token = localStorage.getItem("lumenforge_token");
    
    if (!token) {
      setIsAuthenticated(false);
      setUser(null);
      setIsLoading(false);
      return false;
    }

    if (showLoading) setIsLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/verify?token=${token}`
      );
      const data = await response.json();
      
      // Admin users always have is_vip=true in their token
      if (data.valid && (data.user.is_vip || data.user.is_admin)) {
        setIsAuthenticated(true);
        setUser(data.user);
        setIsLoading(false);
        // Update activity on successful verification
        lastActivityRef.current = Date.now();
        return true;
      } else {
        // Token invalid or no VIP, clear it
        localStorage.removeItem("lumenforge_token");
        setIsAuthenticated(false);
        setUser(null);
        setIsLoading(false);
        return false;
      }
    } catch (error) {
      console.error("Token verification failed (network error):", error);
      // DON'T clear auth on network errors - keep the session alive
      // Only log out when the server explicitly says the token is invalid
      setIsLoading(false);
      return isAuthenticated; // Keep current state
    }
  }, []);

  // Check for session timeout due to inactivity
  const checkSessionTimeout = useCallback(() => {
    const timeSinceActivity = Date.now() - lastActivityRef.current;
    if (timeSinceActivity > SESSION_TIMEOUT && isAuthenticated) {
      console.log("Session timeout due to inactivity");
      localStorage.removeItem("lumenforge_token");
      setIsAuthenticated(false);
      setUser(null);
      window.location.href = "/login?error=session_timeout";
    }
  }, [isAuthenticated]);

  // Initial auth check on mount
  useEffect(() => {
    verifyAuth(true);
  }, [verifyAuth]);

  // Set up activity listeners and periodic checks
  useEffect(() => {
    if (!isAuthenticated) return;

    // Track user activity
    const activityEvents = ["mousedown", "keydown", "scroll", "touchstart"];
    activityEvents.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    // Check for session timeout every minute
    const timeoutCheckInterval = setInterval(checkSessionTimeout, 60 * 1000);

    // Re-verify subscription status periodically
    reverifyIntervalRef.current = setInterval(() => {
      console.log("Re-verifying subscription status...");
      verifyAuth(false);
    }, REVERIFY_INTERVAL);

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(timeoutCheckInterval);
      if (reverifyIntervalRef.current) {
        clearInterval(reverifyIntervalRef.current);
      }
    };
  }, [isAuthenticated, updateActivity, checkSessionTimeout, verifyAuth]);

  const logout = () => {
    localStorage.removeItem("lumenforge_token");
    setIsAuthenticated(false);
    setUser(null);
    if (reverifyIntervalRef.current) {
      clearInterval(reverifyIntervalRef.current);
    }
    window.location.href = "/login";
  };

  // Request access - redirects to login page
  const requestAccess = (callback) => {
    // Store callback path if provided
    const targetPath = callback ? window.location.pathname : "/studio";
    window.location.href = `/login?redirect=${encodeURIComponent(targetPath)}`;
  };

  // Force re-verification (can be called manually)
  const forceReVerify = () => {
    return verifyAuth(true);
  };

  return (
    <SiteAuthContext.Provider value={{ 
      isAuthenticated, 
      isLoading, 
      user, 
      logout, 
      requestAccess,
      forceReVerify,
      updateActivity 
    }}>
      {children}
    </SiteAuthContext.Provider>
  );
}

// HOC to wrap protected routes - redirects to login if not authenticated
export function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useSiteAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Redirect to login, preserving the intended destination
      navigate("/login", { state: { from: location.pathname + location.search } });
    }
  }, [isAuthenticated, isLoading, navigate, location]);

  if (isLoading) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        background: "#0a0a0a",
        color: "#666"
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: 32, height: 32, margin: "0 auto 16px",
            border: "3px solid #333", borderTopColor: "#f59e0b",
            borderRadius: "50%", animation: "spin 1s linear infinite"
          }} />
          <p style={{ fontSize: 14, color: "#888" }}>Checking authentication...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Will redirect via useEffect, show nothing
    return null;
  }

  return children;
}

export default SiteAuthProvider;
